import style from "@/styles/pages/PropertyDetails/PropertyDetails.module.css";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCredentialsComplete } from "@/slices/progressSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import ExistingMemberCard from "@/components/GetStarted/ExistingMemberCard";
import { selectPropertyInfo, setMonthlyRent, setNumOfRentalProperties, setNumOfRentalUnits, setOwnershipPercentage, setOwnOtherRentalProperties, setPurposeOfProperty, setRentOut, setSeparateUnit, setSubjectPropertyRentalUnits } from "@/slices/propertyInfoSlice";
import clsx from "clsx";
import CurrencyInput from "react-currency-input-field";
import { selectPropertyAddress } from "@/slices/propertyAddressSlice";
import { applicationCategory } from "@/types/applicationType";
import ToolTip from "@/components/shared/ToolTip";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { AddressDto, ApplicationDto, PropertyForCreationDto, SubjectPropertyDto, TokenData } from "@/lib/DTMWebAPIClient.generated";
import { setApplicationProgress } from "@/slices/applicationSlice";
import dayjs from "dayjs";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@/context/AuthContext";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function PropertyPurpose() {
    const navigate = useNavigate();

    const [purpose, setPurpose] = useState(-1);

    const dispatch = useDispatch();
    const { login } = useAuth();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const propertyAddress = useSelector(selectPropertyAddress).address;
    const [partiallyRented, setPartiallyRented] = useState("");
    const [separateUnitExists, setSeparateUnitExists] = useState("");
    const [monthlyRentAmount, setMonthlyRentAmount] = useState("");
    const [ownRentalProperties, setOwnRentalProperties] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const { state } = useLocation();

    const methods = useForm({
        defaultValues: {
            purpose: "",
            partiallyRented: "",
            separateUnit: "",
            monthlyRent: "",
            rentalUnitCount: "",
            ownRentalProperties: "",
            ownRentalPropertyCount: "",
            ownRentalUnitCount: "",
            ownershipPercentage: "",
        },
    });

    useEffect(() => {
        document.title = "How will you use this property?";
        //*set profile section complete
        dispatch(setCredentialsComplete(true));
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        switch (propertyInfo.purposeOfProperty) {
            case "I'll live there":
                setPurpose(1);
                methods.setValue("partiallyRented", propertyInfo.rentOut ? "yes" : "no");
                setPartiallyRented(propertyInfo.rentOut ? "yes" : "no");

                methods.setValue("separateUnit", propertyInfo.separateUnit ? "yes" : "no");
                setSeparateUnitExists(propertyInfo.separateUnit ? "yes" : "no");

                methods.setValue("monthlyRent", propertyInfo.monthlyRent == 0 ? "" : "$" + propertyInfo.monthlyRent.toString());
                setMonthlyRentAmount(propertyInfo.monthlyRent == 0 ? "" : propertyInfo.monthlyRent.toString());
                break;
            case "I'll rent it out":
                setPurpose(2);
                methods.setValue("ownRentalProperties", propertyInfo.ownOtherRentalProperties ? "yes" : "no");
                setOwnRentalProperties(propertyInfo.ownOtherRentalProperties ? "yes" : "no");

                methods.setValue("monthlyRent", propertyInfo.monthlyRent == 0 ? "" : "$" + propertyInfo.monthlyRent.toString());
                setMonthlyRentAmount(propertyInfo.monthlyRent == 0 ? "" : propertyInfo.monthlyRent.toString());

                methods.setValue("rentalUnitCount", propertyInfo.subjectPropertyRentalUnits == 0 ? "" : propertyInfo.subjectPropertyRentalUnits.toString());
                methods.setValue("ownRentalPropertyCount", propertyInfo.numOfRentalProperties == 0 ? "" : propertyInfo.numOfRentalProperties.toString());
                methods.setValue("ownRentalUnitCount", propertyInfo.numOfRentalUnits == 0 ? "" : propertyInfo.numOfRentalUnits.toString());
                methods.setValue("ownershipPercentage", propertyInfo.ownershipPercentage == undefined ? "" : propertyInfo.ownershipPercentage.toString());

                break;
            case "It will be my vacation home":
                setPurpose(3);
                methods.setValue("partiallyRented", propertyInfo.rentOut ? "yes" : "no");
                setPartiallyRented(propertyInfo.rentOut ? "yes" : "no");

                methods.setValue("separateUnit", propertyInfo.separateUnit ? "yes" : "no");
                setSeparateUnitExists(propertyInfo.separateUnit ? "yes" : "no");

                methods.setValue("monthlyRent", propertyInfo.monthlyRent == 0 ? "" : "$" + propertyInfo.monthlyRent.toString());
                setMonthlyRentAmount(propertyInfo.monthlyRent == 0 ? "" : propertyInfo.monthlyRent.toString());
                break;
            case "It will be a secondary property":
                setPurpose(4);
                methods.setValue("partiallyRented", propertyInfo.rentOut ? "yes" : "no");
                setPartiallyRented(propertyInfo.rentOut ? "yes" : "no");

                methods.setValue("separateUnit", propertyInfo.separateUnit ? "yes" : "no");
                setSeparateUnitExists(propertyInfo.separateUnit ? "yes" : "no");

                methods.setValue("monthlyRent", propertyInfo.monthlyRent == 0 ? "" : "$" + propertyInfo.monthlyRent.toString());
                setMonthlyRentAmount(propertyInfo.monthlyRent == 0 ? "" : propertyInfo.monthlyRent.toString());
                break;
        }
        methods.setValue("purpose", propertyInfo.purposeOfProperty);

        //Refresh Token if it is expired
        if (credential.credential.expiration && dayjs(credential.credential.expiration).diff(dayjs(), 'minute') < 1) {
            client.apiAuthRefreshtoken(new TokenData({
                accessToken: credential.credential.accessToken,
                refreshToken: credential.credential.refreshToken
            })).then((result) => {
                login(result.accessToken != undefined ? result.accessToken : "", result.refreshToken != undefined ? result.refreshToken : "");
                updateAppData();
            })
        } else {
            updateAppData();
        }

        methods.register("purpose", {
            required: "Please select an option",
        });

        if (state != null) {
            setEditMode(true);
        }
    }, []);

    useEffect(() => {
        if (isFetching && purpose != -1) {
            setIsFetching(false);
            return;
        }
        switch (purpose) {
            case 1:
                if (methods.getValues("ownRentalProperties").trim() != "") {
                    setOwnRentalProperties("no");
                    methods.setValue("ownRentalProperties", "no");
                }
                if (propertyInfo.purposeOfProperty != "I'll live there") {
                    resetPurposeFollowUpQuestions();
                }
                break;
            case 2:
                prefillFollowupQuestions();
                if (propertyInfo.purposeOfProperty != "I'll rent it out") {
                    resetPurposeFollowUpQuestions();
                }
                setMonthlyRentAmount("");
                methods.setValue("monthlyRent", "");
                break;
            case 3:
                prefillFollowupQuestions();
                if (propertyInfo.purposeOfProperty != "It will be my vacation home") {
                    resetPurposeFollowUpQuestions();
                }
                setMonthlyRentAmount("");
                methods.setValue("monthlyRent", "");
                break;
            case 4:
                prefillFollowupQuestions();
                if (propertyInfo.purposeOfProperty != "It will be a secondary property") {
                    resetPurposeFollowUpQuestions();
                }
                setMonthlyRentAmount("");
                methods.setValue("monthlyRent", "");
                break;
        }

    }, [purpose]);

    const onError = (errors: object) => {
        console.log(errors);
        setClickInsightFormErrors(errors);
    }

    const resetPurposeFollowUpQuestions = () => {
        setOwnRentalProperties("");
        methods.setValue("ownRentalProperties", "");
        setPartiallyRented("");
        methods.setValue("partiallyRented", "");
        setSeparateUnitExists("");
        methods.setValue("separateUnit", "");
        setMonthlyRentAmount("");
        methods.setValue("monthlyRent", "");
        methods.setValue("rentalUnitCount", "");
        methods.setValue("ownRentalPropertyCount", "");
        methods.setValue("ownRentalUnitCount", "");
    }

    const prefillFollowupQuestions = () => {
        if (methods.getValues("partiallyRented").trim() != "") {
            setPartiallyRented("no");
            methods.setValue("partiallyRented", "no");
        }
        if (methods.getValues("separateUnit").trim() != "") {
            setSeparateUnitExists("no");
            methods.setValue("separateUnit", "no");
        }
    }

    const updateAppData = () => {
        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            result.propertyAddress = propertyAddress;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }

    const onSubmit = async (data: any) => {
        let occupancyType = "";
        methods.clearErrors();
        switch (purpose) {
            case 1:
                dispatch(setPurposeOfProperty("I'll live there"));
                occupancyType = "I'll live there";
                dispatch(setRentOut(data.partiallyRented === "yes" ? true : false));
                dispatch(setSeparateUnit(data.separateUnit === "yes" ? true : false));
                dispatch(setMonthlyRent((data.partiallyRented === "no" || data.separateUnit === "no") ? 0 : +monthlyRentAmount));
                dispatch(setOwnOtherRentalProperties(false));
                dispatch(setSubjectPropertyRentalUnits(0));
                dispatch(setNumOfRentalProperties(0));
                dispatch(setNumOfRentalUnits(0));
                dispatch(setOwnershipPercentage(undefined));
                break;
            case 2:
                dispatch(setPurposeOfProperty("I'll rent it out"));
                occupancyType = "I'll rent it out";
                dispatch(setOwnOtherRentalProperties(data.ownRentalProperties === "yes" ? true : false));
                dispatch(setSubjectPropertyRentalUnits((+methods.getValues("rentalUnitCount"))));
                dispatch(setNumOfRentalProperties(+methods.getValues("ownRentalPropertyCount")));
                dispatch(setNumOfRentalUnits(+methods.getValues("ownRentalUnitCount")));
                dispatch(setMonthlyRent(+monthlyRentAmount));
                dispatch(setOwnershipPercentage(+methods.getValues("ownershipPercentage")));
                break;
            case 3:
                dispatch(setPurposeOfProperty("It will be my vacation home"));
                occupancyType = "It will be my vacation home";
                dispatch(setRentOut(data.partiallyRented === "yes" ? true : false));
                dispatch(setSeparateUnit(data.separateUnit === "yes" ? true : false));
                dispatch(setMonthlyRent((data.partiallyRented === "no" || data.separateUnit === "no") ? 0 : +monthlyRentAmount));
                dispatch(setOwnOtherRentalProperties(false));
                dispatch(setSubjectPropertyRentalUnits(0));
                dispatch(setNumOfRentalProperties(0));
                dispatch(setNumOfRentalUnits(0));
                dispatch(setOwnershipPercentage(undefined));
                break;
            case 4:
                dispatch(setPurposeOfProperty("It will be a secondary property"));
                occupancyType = "It will be a secondary property";
                dispatch(setRentOut(data.partiallyRented === "yes" ? true : false));
                dispatch(setSeparateUnit(data.separateUnit === "yes" ? true : false));
                dispatch(setMonthlyRent((data.partiallyRented === "no" || data.separateUnit === "no") ? 0 : +monthlyRentAmount));
                dispatch(setOwnOtherRentalProperties(false));
                dispatch(setSubjectPropertyRentalUnits(0));
                dispatch(setNumOfRentalProperties(0));
                dispatch(setNumOfRentalUnits(0));
                dispatch(setOwnershipPercentage(undefined));
                break;
        }

        if (purpose === 2 && data.ownRentalProperties === "") {
            methods.setError("ownRentalProperties", {
                type: "required",
                message: "Please select an option.",
            });
            return;
        }
        // if (purpose !== 2 && data.partiallyRented === "") {
        //     console.log('invalid')
        //     methods.setError("partiallyRented", {
        //         type: "required",
        //         message: "Please select an option.",
        //     });
        //     return;
        // }

        let selectedClosingDate = undefined;
        if (propertyInfo.closingDate != undefined && propertyInfo.closingDate != "") {
            let dateComponents = propertyInfo.closingDate.split("/");
            selectedClosingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }

        let selectedFundingDate = undefined;
        if (propertyInfo.fundingDate != undefined && propertyInfo.fundingDate != "") {
            let dateComponents = propertyInfo.fundingDate.split("/");
            selectedFundingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }

        //Update Property Info on Trellis Backend
        setIsSaving(true);
        await client.apiDealsApplicationsMortgagesSubjectProperty(new PropertyForCreationDto({
            applicationId: 0,
            remainingBalanceAmount: propertyInfo.mortgageBalance,
            downPaymentAmount: application.downPayment,
            totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice - application.downPayment : application.totalLoan,
            newMoneyRequestedAmount: 0,
            subjectProperty: new SubjectPropertyDto({
                originalPurchasePrice: propertyInfo.originalPurchasePrice,
                purchasePrice: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice : propertyInfo.propertyValue,
                propertySalePrice: 0,
                address: new AddressDto({
                    id: 0,
                    streetNumber: propertyAddress.streetNumber,
                    streetName: propertyAddress.streetName,
                    streetType: propertyAddress.streetType,
                    streetDirection: propertyAddress.streetDirection,
                    unitType: propertyAddress.unitType,
                    unitNumber: propertyAddress.unitNumber,
                    city: propertyAddress.city,
                    region: propertyAddress.province,
                    postalCode: propertyAddress.postalCode,
                    country: propertyAddress.country,
                    timeAtResidenceInMonths: 0,
                    residenceStatusType_Name: "",
                    deliveryMode: undefined,
                    modeNumber: undefined,
                    deliveryMode2: undefined,
                    modeNumber2: undefined,
                    isMailing: false
                }),
                propertyTax: propertyInfo.propertyTax,
                propertyTaxFrequency: "annually",
                mortgageHolder: propertyInfo.mortgageHolder,
                livableArea: propertyInfo.livableArea,
                livableAreaType: propertyInfo.livableAreaUnit,
                heatingCost: propertyInfo.heatingCost,
                heatingType: propertyInfo.heatingType,
                heatingCostFrequency: "monthly",
                condoFees: propertyInfo.maintenanceFee,
                condoFeesFrequency: "monthly",
                futureStatusOfProperty: "",
                occupancyType: occupancyType,
                subjectPropertyRentalUnits: +methods.getValues("rentalUnitCount"),
                totalRentalUnits: +methods.getValues("ownRentalUnitCount"),
                totalRentalProperties: +methods.getValues("ownRentalPropertyCount"),
                ownershipPercentage: +methods.getValues("ownershipPercentage"),
                hasCurrentRentalProperties: data.ownRentalProperties === "yes",
                isPlanningOnRenting: data.partiallyRented === "yes",
                isSeparateLegalUnit: data.separateUnit === "yes",
                rent: +monthlyRentAmount,
                estimatedFundingDate: selectedFundingDate,
                closingDate: selectedClosingDate,
                conditionOfFinancingDate: undefined,
                rentalIncome: +monthlyRentAmount,
            })
        })).then((result) => {

            nextStage();

        }).catch((error) => {

            console.log("Error saving property information", error);
            nextStage();
        });
    };

    const handleClick = (type: number) => {
        setPurpose(type);
        switch (type) {
            case 1:
                methods.setValue("purpose", "I'll live there");
                if (ownRentalProperties != "") {
                    setOwnRentalProperties("no");
                }
                methods.setValue("rentalUnitCount", "");
                break;
            case 2:
                methods.setValue("purpose", "I'll rent it out");
                break;
            case 3:
                methods.setValue("purpose", "It will be my vacation home");
                if (ownRentalProperties != "") {
                    setOwnRentalProperties("no");
                }
                methods.setValue("rentalUnitCount", "");
                break;
            case 4:
                methods.setValue("purpose", "It will be a secondary property");
                if (ownRentalProperties != "") {
                    setOwnRentalProperties("no");
                }
                methods.setValue("rentalUnitCount", "");
                break;
        }
        methods.clearErrors();
    }

    const nextStage = () => {
        setIsSaving(false);
        dispatch(setApplicationProgress(15));
        setClickInsightApplicationProgress('Your new home - How will you use this property?');
        switch (application.applicationCategory) {
            case applicationCategory.Buy:
                navigate("/single/financing-condition");
                break;
            case applicationCategory.Refinance:
            case applicationCategory.Transfer:
                if (editMode){
                    navigate(-2);
                }else{
                    dispatch(setApplicationProgress(16));
                    navigate("/single/review-application");
                    break;
                }
        }
    }

    const partiallyRentedChangeHandler = (e: any) => {
        setPartiallyRented(e.target.value);
        if (e.target.value === "no") {
            methods.setValue("separateUnit", e.target.value);
            setSeparateUnitExists(e.target.value);
        }
        methods.setValue("partiallyRented", e.target.value);
    };

    const separateUnitChangeHandler = (e: any) => {
        setSeparateUnitExists(e.target.value);
        methods.setValue("separateUnit", e.target.value);
    };

    const ownPropertiesChangeHandler = (e: any) => {
        setOwnRentalProperties(e.target.value);
        methods.setValue("ownRentalPropertyCount", "");
        methods.setValue("ownRentalUnitCount", "");
        methods.setValue("ownRentalProperties", e.target.value);
    };

    const displayRentalIncomeQuestion = () => {
        return (<div className="flex md:flex-row flex-col w-max mt-5">
            <div>
                <label htmlFor="title">Monthly Rent</label>
                <div className="flex relative">
                    <span>
                        <img
                            src="assets/error-icon.svg"
                            className={clsx(
                                methods.formState?.errors?.monthlyRent
                                    ? ""
                                    : "hidden",
                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                            )}
                            alt="error"
                            height={5}
                            width={5}
                        />
                    </span>
                    <CurrencyInput
                        id="monthlyRent"
                        placeholder="$"
                        prefix={"$"}
                        decimalsLimit={2}
                        decimalScale={2}
                        allowNegativeValue={false}
                        value={monthlyRentAmount}
                        maxLength={7}
                        className={clsx(
                            methods.formState?.errors?.monthlyRent?.message ? "error" : "",
                            "mt-2 w:[220px] md:w-[250px]"
                        )}
                        {...methods.register("monthlyRent", {
                            required: "Enter the monthly rent of this unit",
                        })}
                        onValueChange={(value, name, values) => value != undefined ? setMonthlyRentAmount(value) : setMonthlyRentAmount("")}
                    />
                </div>
                {methods.formState?.errors?.monthlyRent?.message && (
                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                        {methods.formState.errors.monthlyRent.message}
                    </span>
                )}
            </div>
        </div>)
    }

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">How will you use this property?</h1>
                        <p className="sub-heading">Help us understand the purpose of this property.</p>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="content-container">
                                        <div className="grid grid-cols-2 md:grid-cols-2 xlg:grid-cols-4">
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(1)}>
                                                    <ExistingMemberCard
                                                        cardHeight={220}
                                                        cardWidth={215}
                                                        imageSrc="assets/main-residence.svg"
                                                        cardText="I'll live there"
                                                        selected={purpose == 1}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(2)}>
                                                    <ExistingMemberCard
                                                        cardHeight={220}
                                                        cardWidth={215}
                                                        imageSrc="assets/rent-property.svg"
                                                        cardText="I'll rent it out"
                                                        selected={purpose == 2}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(3)}>
                                                    <ExistingMemberCard
                                                        cardHeight={220}
                                                        cardWidth={215}
                                                        imageSrc="assets/vacation-property.svg"
                                                        cardText="It will be my vacation home"
                                                        selected={purpose == 3}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex-row mb-5 sm:w-full">
                                                <div onClick={() => handleClick(4)}>
                                                    <ExistingMemberCard
                                                        cardHeight={220}
                                                        cardWidth={215}
                                                        imageSrc="assets/secondary-property.svg"
                                                        cardText="It will be a secondary property"
                                                        selected={purpose == 4}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {methods.formState?.errors?.purpose?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs ml-1">
                                            {methods.formState.errors.purpose.message}
                                        </span>
                                    )}

                                    {(purpose == 1) &&
                                        <div>
                                            <div className="grid grid-flow-row sm:grid-flow-col">
                                                <label className="mb-2">Are you planning to rent out part of your home?</label>
                                            </div>
                                            <div className="grid grid-flow-row sm:grid-flow-col mb-6">
                                                <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                    <li className="relative">
                                                        <input
                                                            type="radio"
                                                            value="yes"
                                                            id="yes-partial-rent"
                                                            {...methods.register("partiallyRented", {
                                                                onChange: partiallyRentedChangeHandler,
                                                            })}
                                                            checked={partiallyRented === "yes"}
                                                            onChange={partiallyRentedChangeHandler}
                                                            className="sr-only peer "
                                                        />
                                                        <label
                                                            className={clsx(
                                                                style.phoneLabel,
                                                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                methods.formState?.errors?.partiallyRented?.message
                                                                    ? style.labelError
                                                                    : ""
                                                            )}
                                                            htmlFor="yes-partial-rent"
                                                        >
                                                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                        </label>
                                                    </li>
                                                    <li className="relative">
                                                        <input
                                                            type="radio"
                                                            value="no"
                                                            id="no-partial-rent"
                                                            {...methods.register("partiallyRented", {
                                                                onChange: partiallyRentedChangeHandler,
                                                            })}
                                                            checked={partiallyRented === "no"}
                                                            onChange={partiallyRentedChangeHandler}
                                                            className="sr-only peer leading-6"
                                                        />
                                                        <label
                                                            className={clsx(
                                                                style.phoneLabel,
                                                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                methods.formState?.errors?.partiallyRented?.message
                                                                    ? style.labelError
                                                                    : ""
                                                            )}
                                                            htmlFor="no-partial-rent"
                                                        >
                                                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                        </label>
                                                    </li>
                                                </ul>
                                            </div>
                                            {methods.formState?.errors?.partiallyRented?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState.errors.partiallyRented.message}
                                                </span>
                                            )}
                                        </div>
                                    }

                                    {partiallyRented === "yes" &&
                                        <div>
                                            <div className="flex mt-5" style={{ maxWidth: 260 }}>
                                                <span className="mr-1"> Is it a separate and legal unit?</span>
                                                <span className="ml-2">
                                                    <ToolTip
                                                        title="What is a legal unit?"
                                                        content="A legal rental unit must fully comply  with all the applicable fire codes, building codes, Electrical Safety Authority regulations, and zoning and housing standards by-laws."
                                                        classNames={style.sinToolTip}
                                                    />
                                                </span>
                                            </div>
                                            <div className="grid grid-flow-row sm:grid-flow-col mb-6">
                                                <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                    <li className="relative">
                                                        <input
                                                            type="radio"
                                                            value="yes"
                                                            id="yes-separate-unit"
                                                            {...methods.register("separateUnit", {
                                                                onChange: separateUnitChangeHandler,
                                                            })}
                                                            checked={separateUnitExists === "yes"}
                                                            onChange={separateUnitChangeHandler}
                                                            className="sr-only peer "
                                                        />
                                                        <label
                                                            className={clsx(
                                                                style.phoneLabel,
                                                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                methods.formState?.errors?.separateUnit?.message
                                                                    ? style.labelError
                                                                    : ""
                                                            )}
                                                            htmlFor="yes-separate-unit"
                                                        >
                                                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                        </label>
                                                    </li>
                                                    <li className="relative">
                                                        <input
                                                            type="radio"
                                                            value="no"
                                                            id="no-separate-unit"
                                                            {...methods.register("separateUnit", {
                                                                onChange: separateUnitChangeHandler,
                                                            })}
                                                            checked={separateUnitExists === "no"}
                                                            onChange={separateUnitChangeHandler}
                                                            className="sr-only peer leading-6"
                                                        />
                                                        <label
                                                            className={clsx(
                                                                style.phoneLabel,
                                                                "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                methods.formState?.errors?.separateUnit?.message
                                                                    ? style.labelError
                                                                    : ""
                                                            )}
                                                            htmlFor="no-separate-unit"
                                                        >
                                                            <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                        </label>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    }

                                    {separateUnitExists === "yes" &&
                                        displayRentalIncomeQuestion()
                                    }

                                    {purpose == 2 &&
                                        <>
                                            {purpose == 2 &&
                                                displayRentalIncomeQuestion()
                                            }
                                            <div className="mt-5">
                                                <label htmlFor="ownershipPercentage">Ownership Percentage</label>
                                                <div className="flex relative mb-3">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.ownershipPercentage ? "" : "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            methods.formState?.errors?.ownershipPercentage?.message
                                                                ? "error"
                                                                : "",
                                                            "mt-2"
                                                        )}
                                                        {...methods.register("ownershipPercentage", {
                                                            required: "Enter the ownership of this property in percentage value.",
                                                            valueAsNumber: true,
                                                            validate: {
                                                                wholePercent: (v) =>
                                                                    Number.isInteger(v) && +v > 0 && +v <= 100
                                                                        ? true
                                                                        : "Please enter a whole number between 1 and 100.",
                                                            },
                                                        })}
                                                        type="number"
                                                        id="ownershipPercentage"
                                                        placeholder="%"
                                                        step={1}
                                                        inputMode="numeric"
                                                    />
                                                </div>
                                                {methods.formState?.errors?.ownershipPercentage?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.ownershipPercentage.message}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-5">
                                                <label htmlFor="rentalUnitCount">How many rental units are in this property?</label>
                                                <div className="flex relative mb-3">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.rentalUnitCount ? "" : "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            methods.formState?.errors?.rentalUnitCount?.message
                                                                ? "error"
                                                                : "",
                                                            "mt-2"
                                                        )}
                                                        {...methods.register("rentalUnitCount", {
                                                            required: "Enter the number of rental units in this property.",
                                                            validate: {
                                                                propertyCount: (v) => {
                                                                    if (methods.getValues("purpose") == "I'll rent it out" && +v > 0) {
                                                                        return true;
                                                                    } else {
                                                                        return "Please enter a valid number.";
                                                                    }

                                                                },
                                                            }
                                                        })}
                                                        type="number"
                                                        id="rentalUnitCount"
                                                    />
                                                </div>
                                                {methods.formState?.errors?.rentalUnitCount?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.rentalUnitCount.message}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-5">
                                                <div className="grid grid-flow-row sm:grid-flow-col">
                                                    <label className="mb-2">Do you currently own any rental properties?</label>
                                                </div>
                                                <div className="grid grid-flow-row sm:grid-flow-col mb-6">
                                                    <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                        <li className="relative">
                                                            <input
                                                                type="radio"
                                                                value="yes"
                                                                id="yes-own-rental-properties"
                                                                {...methods.register("ownRentalProperties", {
                                                                    onChange: ownPropertiesChangeHandler,
                                                                })}
                                                                checked={ownRentalProperties === "yes"}
                                                                onChange={ownPropertiesChangeHandler}
                                                                className="sr-only peer "
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.phoneLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                    methods.formState?.errors?.ownRentalProperties?.message
                                                                        ? style.labelError
                                                                        : ""
                                                                )}
                                                                htmlFor="yes-own-rental-properties"
                                                            >
                                                                <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                            </label>
                                                        </li>
                                                        <li className="relative">
                                                            <input
                                                                type="radio"
                                                                value="no"
                                                                id="no-own-rental-properties"
                                                                {...methods.register("ownRentalProperties", {
                                                                    onChange: ownPropertiesChangeHandler,
                                                                })}
                                                                checked={ownRentalProperties === "no"}
                                                                onChange={ownPropertiesChangeHandler}
                                                                className="sr-only peer leading-6"
                                                            />
                                                            <label
                                                                className={clsx(
                                                                    style.phoneLabel,
                                                                    "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                                    methods.formState?.errors?.ownRentalProperties?.message
                                                                        ? style.labelError
                                                                        : ""
                                                                )}
                                                                htmlFor="no-own-rental-properties"
                                                            >
                                                                <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                            </label>
                                                        </li>
                                                    </ul>
                                                </div>
                                                {methods.formState?.errors?.ownRentalProperties?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.ownRentalProperties.message}
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    }

                                    {ownRentalProperties === "yes" &&
                                        <>
                                            <div>
                                                <label htmlFor="dependentsCount">Total number of rental properties</label>
                                                <div className="flex relative mb-3">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.ownRentalPropertyCount ? "" : "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            methods.formState?.errors?.ownRentalPropertyCount?.message
                                                                ? "error"
                                                                : "",
                                                            "mt-2"
                                                        )}
                                                        {...methods.register("ownRentalPropertyCount", {
                                                            required: "Enter the number of rental properties you currently own.",
                                                            validate: {
                                                                propertyCount: (v) => {
                                                                    if (methods.getValues("ownRentalProperties") == "no" || +v > 0) {
                                                                        return true;
                                                                    } else {
                                                                        return "Please enter a valid number.";
                                                                    }

                                                                },
                                                            }
                                                        })}
                                                        type="number"
                                                        id="rentalPropertyCount"
                                                    />
                                                </div>
                                                {methods.formState?.errors?.ownRentalPropertyCount?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.ownRentalPropertyCount.message}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="mt-5">
                                                <label htmlFor="rentalUnitCount">Total number of rental units</label>
                                                <div className="flex relative mb-3">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.ownRentalUnitCount ? "" : "hidden",
                                                                "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                        />
                                                    </span>
                                                    <input
                                                        className={clsx(
                                                            methods.formState?.errors?.ownRentalUnitCount?.message
                                                                ? "error"
                                                                : "",
                                                            "mt-2"
                                                        )}
                                                        {...methods.register("ownRentalUnitCount", {
                                                            required: "Enter the number of rental units you currently own.",
                                                            validate: {
                                                                unitCount: (v) => {
                                                                    if (methods.getValues("ownRentalProperties") == "no" || +v > 0) {
                                                                        return true;
                                                                    } else {
                                                                        return "Please enter a valid number.";
                                                                    }

                                                                },
                                                            }
                                                        })}
                                                        type="number"
                                                        id="rentalUnitCount"
                                                    />
                                                </div>
                                                {methods.formState?.errors?.ownRentalUnitCount?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState.errors.ownRentalUnitCount.message}
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    }

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                {editMode ? "Save" : "Continue"}
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}